import { useState } from "react";
import { Check, Copy, X, MessageCircle, Mail } from "lucide-react";

/** Bilingual copy, following the same EN/ES pattern as InstallInstructions.jsx. */
const COPY = {
  en: {
    eyebrow: "Founding academies — first 10 only",
    title: "Refer another academy",
    linkLabel: "Signup link",
    copy: "Copy",
    copied: "Copied",
    orSend: "Or send the whole message",
    copyMessage: "Copy message",
    whatsapp: "WhatsApp",
    email: "Email",
    done: "Done",
    langToggle: "Español",
    emailSubject: "Founding academies on RollOSS — first 10 only",
    message: (academyName, link) =>
      `I run ${academyName || "my academy"} on RollOSS — class scheduling, QR check-in, belt tracking and billing all in one place. They're onboarding founding academies right now, first 10 only: 50% off any plan, locked in for 12 months.\n\nTake a look: ${link}`,
  },
  es: {
    eyebrow: "Academias fundadoras — solo las primeras 10",
    title: "Recomienda otra academia",
    linkLabel: "Enlace de registro",
    copy: "Copiar",
    copied: "Copiado",
    orSend: "O envía el mensaje completo",
    copyMessage: "Copiar mensaje",
    whatsapp: "WhatsApp",
    email: "Correo",
    done: "Listo",
    langToggle: "English",
    emailSubject: "Academias fundadoras en RollOSS — solo las primeras 10",
    message: (academyName, link) =>
      `Uso RollOSS para llevar ${academyName || "mi academia"} — horario de clases, check-in con QR, seguimiento de cinturones y cobros, todo en un solo lugar. Están sumando academias fundadoras ahora mismo, solo las primeras 10: 50% de descuento en cualquier plan, fijo por 12 meses.\n\nÉchale un vistazo: ${link}`,
  },
};

export default function ReferAcademyModal({ academyName, defaultLang = "en", onClose }) {
  const [lang, setLang] = useState(defaultLang);
  const c = COPY[lang];
  const link = `${window.location.origin}/pricing`;
  const [copied, setCopied] = useState("");
  const message = c.message(academyName, link);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1800);
    } catch (e) { /* ignore */ }
  };

  const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent(c.emailSubject)}&body=${encodeURIComponent(message)}`;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "grid", placeItems: "center", zIndex: 60 }} onClick={onClose}>
      <div className="card p-7 w-[520px] max-w-[92vw]" onClick={e => e.stopPropagation()} data-testid="refer-academy-modal">
        <div className="flex items-start justify-between mb-5 gap-3">
          <div>
            <div className="label" style={{ color: "var(--green)" }}>{c.eyebrow}</div>
            <div className="text-[20px] font-bold uppercase mt-1 tracking-tight">{c.title}</div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              className="label-tiny underline"
              style={{ color: "var(--text-dim)" }}
              data-testid="refer-lang-toggle"
            >
              {c.langToggle}
            </button>
            <button onClick={onClose} data-testid="refer-close"><X size={18} /></button>
          </div>
        </div>

        <div className="label mb-2">{c.linkLabel}</div>
        <div className="card flex items-center gap-2 p-3" data-testid="refer-link">
          <code className="flex-1 mono text-[13px] truncate" style={{ color: "var(--green)" }}>{link}</code>
          <button onClick={() => copy(link, "link")} className="btn btn-outline" style={{ padding: "6px 12px", fontSize: 11 }} data-testid="refer-copy-link-btn">
            {copied === "link" ? <><Check size={12} />{c.copied}</> : <><Copy size={12} />{c.copy}</>}
          </button>
        </div>

        <div className="label mt-5 mb-2">{c.orSend}</div>
        <div className="card p-3" data-testid="refer-message">
          <pre className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text)", fontFamily: "inherit" }}>{message}</pre>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={() => copy(message, "msg")} className="btn btn-outline" data-testid="refer-copy-msg-btn">
            {copied === "msg" ? <><Check size={12} />{c.copied}</> : <><Copy size={12} />{c.copyMessage}</>}
          </button>
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline" data-testid="refer-share-whatsapp">
            <MessageCircle size={12} /> {c.whatsapp}
          </a>
          <a href={mailUrl} className="btn btn-outline" data-testid="refer-share-email">
            <Mail size={12} /> {c.email}
          </a>
        </div>

        <button onClick={onClose} className="btn btn-primary w-full mt-5" data-testid="refer-done">{c.done}</button>
      </div>
    </div>
  );
}
